/**
 * The shared frame every glyph on the site draws inside.
 *
 * The site installs no icon font (FR-021(b)) — not `react-native-vector-icons`,
 * not `@expo/vector-icons`, not any icon package. Icons are inline SVG instead,
 * which costs a few hundred bytes each in the bundle the visitor already
 * downloads rather than a whole extra font request, and keeps the delivery
 * budget honest.
 *
 * That decision has a second, less obvious payoff. React Native Paper takes a
 * render function anywhere it takes an `icon`, so a glyph slots into Paper
 * without any adapter:
 *
 *   <IconButton icon={({ size, color }) => <MoonGlyph size={size} color={color} />} />
 *
 * When Paper is handed a render function it calls it directly, so the `□`
 * placeholder Paper falls back to when it cannot resolve a *named* icon never
 * appears — which matters, because that placeholder is a bare glyph with no
 * accessible name and would be pure noise if it were ever announced. The other
 * half of avoiding it is staying away from the Paper components that render an
 * internal icon whether you ask for one or not: `Checkbox`, `Searchbar`,
 * `TextInput.Icon`, and `Banner` with an icon.
 *
 * Raw DOM `<svg>` is deliberate here. Everything else on the site goes through
 * react-native-web for the sake of the shared Vault theme, but this tree only
 * ever renders in a browser, and `react-native-svg` would be a dependency
 * bought for nothing.
 *
 * ## Named or silent (FR-019(b))
 *
 * A glyph is **decorative by default** and is hidden from assistive technology.
 * Nearly every glyph here sits inside a control that already carries its own
 * `accessibilityLabel`, or beside a heading that already says the same thing;
 * announcing the glyph as well would just say it twice. Pass `label` only when
 * the glyph is the *sole* carrier of the meaning — a control with no visible
 * text and no label of its own.
 *
 * Defaulting to silent is the safe direction to fail in: a missing name shows
 * up on one control and is easy to spot in an audit, while a spurious one
 * repeats on every control that reuses the glyph.
 */

import type { ReactNode } from "react";
import { iconSize } from "@/theme/tokens";

export interface GlyphProps {
  /** Box edge in CSS px. Paper passes this straight through. */
  size?: number;
  /** Stroke colour. Paper passes this straight through. */
  color?: string;
  /**
   * Accessible name. Leave it off (the usual case) and the glyph is hidden
   * from assistive technology — see the note above before adding one.
   */
  label?: string;
}

/**
 * A calm, even stroke rather than solid fills: it holds up at the 16 px card
 * sizes and the 24 px control sizes alike, and it inherits the theme's text
 * colour without any per-scheme branching.
 */
const STROKE_WIDTH = 1.75;

/**
 * `block` keeps the inline-SVG baseline gap from showing up if a glyph ever
 * lands outside a flex container; `flexShrink: 0` stops a tight row from
 * squashing a glyph out of its square.
 */
const GLYPH_STYLE = { display: "block", flexShrink: 0 } as const;

export function Glyph({
  size = iconSize.lg,
  color = "currentColor",
  label,
  children,
}: GlyphProps & { children: ReactNode }) {
  const isNamed = label !== undefined && label !== "";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={STROKE_WIDTH}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={isNamed ? "img" : undefined}
      aria-label={isNamed ? label : undefined}
      aria-hidden={isNamed ? undefined : true}
      focusable="false"
      style={GLYPH_STYLE}
    >
      {children}
    </svg>
  );
}
